
import React, { useCallback, useMemo } from "react";
import { Handle } from "@xyflow/react";

// i18n
import { useTranslation } from "react-i18next";

// MUI
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

// Contexts
import { useMapContext, useMasteryMap } from "../../context";

// Components
import Badge from "../SubUnitRow/Badge";

// Styles
import styles from "./SmartNote.module.scss";
import MenuButton from "../../../../components/MenuButton";

// Assets
import videoIcon from "images/icons/video.png";
import quizIcon from "images/icons/quiz.png";
import podcastIcon from "images/icons/podcast.png";
import flashcardIcon from "images/icons/cards.png";

const SmartNote = ({ data }) => {
  const { t } = useTranslation();

  // Context
  const { onGenerateModal, onGenerateQuizModal } = useMasteryMap();
  const { onDeepDive, onPractice } = useMapContext();

  // Memoized
  const noteUrl = useMemo(() => `/notes/${data.id}`, [data.id]);

  // Handlers
  const handleDeepDiveClick = useCallback(() => {
    if (data.progress >= 70) {
      onDeepDive(data.id);
    }
  }, [onDeepDive, data]);

  const handlePracticeClick = useCallback(() => onPractice(data.id), [onPractice, data.id]);

  const handleGenerateOption = useCallback((option) => {
    switch (option.value) {
      case 'video':
        // TODO: PASS MORE DATA TO THE MODAL
        onGenerateModal('video');
        break;
      case 'quiz':
        // TODO: PASS MORE DATA TO THE MODAL
        onGenerateQuizModal();
        break;
      case 'podcast':
        // TODO: PASS MORE DATA TO THE MODAL
        onGenerateModal('podcast');
        break;
      case 'flashcard':
        break;
      default:
        break;
    }
  }, []);

  const handleTitleClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    Turbo.visit(e.currentTarget.href);
  }, []);
  const handleSeeNoteClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    Turbo.visit(noteUrl);
  }, [noteUrl]);

  // Render
  const allowDeepDive = data.progress >= 70;

  const renderGenerateOption = (option) => {
    let icon = ''
    let label = ''

    switch (option.value) {
      case 'video':
        icon = <img src={videoIcon} alt='Video' className={`${styles.generateIcon}`} />
        label = t('masteryMap.map.generate.video');
        break;
      case 'quiz':
        icon = <img src={quizIcon} alt='Quiz' className={`${styles.generateIcon}`} />
        label = t('masteryMap.map.generate.quiz');
        break;
      case 'podcast':
        icon = <img src={podcastIcon} alt='Podcast' className={`${styles.generateIcon}`} />
        label = t('masteryMap.map.generate.podcast');
        break;
      case 'flashcard':
        icon = <img src={flashcardIcon} alt='Flashcard' className={`${styles.generateIcon}`} />
        label = t('masteryMap.map.generate.flashcard');
        break;
      default:
        break;
    }

    return (
      <div className='flex items-center gap-2 text-blue-500'>
        {icon}
        {label}
      </div>
    )
  }

  const renderActions = () => {
    return (
      <div className={`${styles.actions}`}>
        {/* Practice Button */}
        <Button
          className={styles.actionButton}
          color='primary'
          variant='text'
          onClick={handlePracticeClick}
        >
          {t('masteryMap.actions.practice')}
        </Button>

        {/* Deep Dive Button */}
        {allowDeepDive && (
          <Button
            className={styles.actionButton}
            color='primary'
            variant='contained'
            onClick={handleDeepDiveClick}
            disabled={data.expanded}
          >
            {t('masteryMap.actions.deep_dive')}
          </Button>
        )}

        <MenuButton
          options={[
            { label: t('masteryMap.map.generate.video'), value: 'video' },
            { label: t('masteryMap.map.generate.quiz'), value: 'quiz' },
            { label: t('masteryMap.map.generate.podcast'), value: 'podcast' },
            { label: t('masteryMap.map.generate.flashcard'), value: 'flashcard' },
          ]}
          className={`${styles.actionButton} justify-center`}
          onSelect={handleGenerateOption}
          renderOption={renderGenerateOption}
        >
          {t('masteryMap.actions.generate')}
        </MenuButton>
      </div>
    )
  }

  return (
    <div id={data.id} className={styles.subUnitNode}>
      <div className={styles.header}>
        <Typography variant="body2" color="textSecondary">
          {t('masteryMap.map.smart_note')}
        </Typography>

        <div className={`${styles.headerActions}`}>
          <Button variant='text' color='primary' onClick={handleSeeNoteClick}>
            {t('masteryMap.map.actions.see_note')}
          </Button>
        </div>
      </div>

      <div className={`${styles.content} drag-handle`}>
        <div>
          <Badge familiarity={data.progress} isLocked={false} />
        </div>

        <Typography
          variant="body2"
          color="textPrimary"
          component={'a'}
          href={noteUrl}
          className={styles.title}
          onClick={handleTitleClick}
        >
          {data.title}
        </Typography>
      </div>

      {renderActions()}

      <Handle type="source" position="right" style={{ opacity: 0, pointerEvents: 'none' }} />
      <Handle type="target" position="left" style={{ opacity: 0, pointerEvents: 'none' }} />
    </div>
  )
}

export default SmartNote;
